import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '@gonfalon/async/useDebouncedValue';
import { MemberAvatar } from '@gonfalon/members/MemberAvatar';
import { memberInitials } from '@gonfalon/members/memberInitials';
import { useGetAiConversationHistoryQuery } from '@gonfalon/observability-graphql/hooks';
import { Box } from '@gonfalon/observability-ui/components/Box';
import { LiveModeIndicator } from '@gonfalon/observability-ui/components/LiveModeIndicator';
import { PAGE_SIZE, SearchPagination } from '@gonfalon/observability-ui/components/SearchPagination';
import { Stack } from '@gonfalon/observability-ui/components/Stack';
import { Tabs } from '@gonfalon/observability-ui/components/Tabs';
import { Text } from '@gonfalon/observability-ui/components/Text';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';
import { themeVars } from '@gonfalon/observability-ui/theme';
import { useMembers } from '@gonfalon/rest-api/hooks/useMembers';
import {
  Group,
  IconButton,
  Input,
  Button as LaunchpadButton,
  SearchField,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import type { IconName } from '@launchpad-ui/icons';

import { unBuildPrompt } from '../../agentInsightsUtils';
import { CONVERSATION_TAG_KEYS } from '../../conversationTags';
import { formatRelativeDate } from '../../utils/formatRelativeDate';
import { getSafeHttpUrl } from '../../utils/safeUrl';

import * as styles from './AgentInsights.css';

type HistoryTab = 'personal' | 'shared' | 'auto-remediations' | 'flag-cleanups';

type ConversationTags = ReadonlyArray<{ key: string; value: string }> | null | undefined;

interface TabDefinition {
  id: HistoryTab;
  label: string;
  emptyMessage: string;
  variables: {
    shared_only?: boolean;
    tag_key?: string;
    tag_value?: string;
  };
  /**
   * Optional client-side filter applied AFTER the server query — used for
   * tabs whose semantics can't be expressed by the GraphQL filters (which
   * only support a single key/value pair). When present, the rendered list
   * reflects the filter but pagination still uses the server's totalCount,
   * which can produce short pages. This is acceptable for narrow filters
   * like "open PR" where the result set is small and transient.
   */
  clientFilter?: (tags: ConversationTags) => boolean;
}

// Tag-filtered tabs intentionally omit `shared_only` so they show the full set of
// matching conversations (the user's own + any shared with them) — narrowing those
// further would hide chats that are clearly relevant to the filter.
const TAB_DEFINITIONS: readonly TabDefinition[] = [
  {
    id: 'personal',
    label: 'Personal',
    emptyMessage: 'No personal chats yet',
    variables: { shared_only: false },
  },
  {
    id: 'shared',
    label: 'Shared',
    emptyMessage: 'No chats have been shared with you',
    variables: { shared_only: true },
  },
  {
    id: 'auto-remediations',
    label: 'Auto remediations',
    emptyMessage: 'No auto remediations yet',
    variables: { tag_key: CONVERSATION_TAG_KEYS.autoInvestigation, tag_value: 'true' },
  },
  {
    id: 'flag-cleanups',
    label: 'Flag cleanups',
    emptyMessage: 'No flag cleanups yet',
    variables: { tag_key: CONVERSATION_TAG_KEYS.flagCleanupInProgress },
  },
];

const TITLE_FALLBACK_LENGTH = 30;
const SEARCH_DEBOUNCE_MS = 250;
const HISTORY_POLL_INTERVAL_MS = 5000;

// A conversation still flagged `active` after this long almost certainly stalled
// (the agent crashed, a webhook never landed, etc.) rather than genuinely running,
// so we surface a warning instead of the live "in progress" indicator.
const STALE_ACTIVE_THRESHOLD_MS = 90 * 60 * 1000;

function isEmail(value: string): boolean {
  return value.includes('@');
}

function deriveTitle(conversation: Pick<ConversationSummary, 'title' | 'message_preview'>): string {
  const title = conversation.title?.trim();
  if (title) {
    return title;
  }
  const cleaned = unBuildPrompt(conversation.message_preview).trim();
  if (cleaned.length <= TITLE_FALLBACK_LENGTH) {
    return cleaned || 'Untitled chat';
  }
  return `${cleaned.slice(0, TITLE_FALLBACK_LENGTH).trimEnd()}…`;
}

interface ConversationHistoryViewProps {
  onOpenConversation: (conversationId: string) => void;
  currentUserId?: string;
  currentUserEmail?: string;
  aiChatLimitData?: {
    aiChatLimitStatus: {
      current_count: number;
      limit: number;
    } | null;
  };
  usageHref?: string;
}

export const ConversationHistoryView: React.FC<ConversationHistoryViewProps> = ({
  onOpenConversation,
  currentUserId,
  currentUserEmail,
  aiChatLimitData,
  usageHref,
}) => {
  const { projectId } = useProjectId();
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState<HistoryTab>('personal');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  const activeTabDefinition = TAB_DEFINITIONS.find((t) => t.id === activeTab) ?? TAB_DEFINITIONS[0];

  // Reset to the first page whenever the filter set changes so users aren't
  // stranded on an empty page when the result set shrinks.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeTab]);

  const titleSearchVariable = debouncedSearch.trim() || undefined;

  const { data, loading, error } = useGetAiConversationHistoryQuery({
    variables: {
      project_id: projectId,
      count: PAGE_SIZE,
      page,
      ...activeTabDefinition.variables,
      query: titleSearchVariable,
    },
    skip: !projectId,
    fetchPolicy: 'cache-and-network',
    // Poll while the view is mounted so in-flight conversations visibly flip
    // from "active" to their terminal status without a manual refresh. Apollo
    // pauses polling automatically while the tab is backgrounded.
    pollInterval: HISTORY_POLL_INTERVAL_MS,
  });

  // Memoize the conversations slice so dependents (e.g. the unresolved-ids
  // useMemo below) don't re-evaluate every render when Apollo returns the
  // same underlying data object.
  const conversations = useMemo(
    () => data?.aiConversationHistory?.conversations ?? [],
    [data?.aiConversationHistory?.conversations],
  );
  const totalCount = data?.aiConversationHistory?.totalCount ?? 0;

  // Apply the active tab's client-side filter (e.g. "open PRs" excludes any
  // conversation that also has a `pr-merged` or `pr-closed` tag). When no
  // filter is defined this is a no-op and returns the same array reference.
  const displayedConversations = useMemo(() => {
    const filter = activeTabDefinition.clientFilter;
    if (!filter) {
      return conversations;
    }
    return conversations.filter((c) => filter(c.tags));
  }, [conversations, activeTabDefinition.clientFilter]);

  const isOwnConversation = useCallback(
    (ownerValue: string) => ownerValue === currentUserId || ownerValue === currentUserEmail,
    [currentUserId, currentUserEmail],
  );

  // Resolve member ids for any conversation we'll surface where the owner isn't
  // the current user — covers the Shared/All/tag-filtered tabs where someone
  // else's email/id might appear on the card.
  const unresolvedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of conversations) {
      if (!isOwnConversation(c.user_email) && c.user_email && !isEmail(c.user_email)) {
        ids.add(c.user_email);
      }
    }
    return Array.from(ids);
  }, [conversations, isOwnConversation]);

  const { data: membersData } = useMembers(
    {
      params: {
        filter: { ids: unresolvedIds },
        limit: 50,
      },
    },
    { enabled: unresolvedIds.length > 0 },
  );

  const memberDisplayMap = useMemo(() => {
    const map = new Map<string, { displayName: string; email: string; initials: string | undefined }>();
    if (membersData?.items) {
      for (const m of membersData.items) {
        map.set(m._id, {
          displayName: m.firstName && m.lastName ? `${m.firstName} ${m.lastName}` : m.email,
          email: m.email,
          initials: memberInitials({ firstName: m.firstName, lastName: m.lastName }),
        });
      }
    }
    return map;
  }, [membersData]);

  const hasActiveSearch = titleSearchVariable !== undefined;
  const emptyMessage = hasActiveSearch ? 'No chats match your search' : activeTabDefinition.emptyMessage;

  return (
    <Box height="full" display="flex" flexDirection="column">
      <Box px="16" pt="12" pb="8" flexShrink={0}>
        <SearchField
          aria-label="Search chats"
          value={searchInput}
          onChange={setSearchInput}
          onClear={() => setSearchInput('')}
          style={{ width: '100%' }}
        >
          <Group>
            <Icon name="search" size="small" />
            <Input placeholder="Search…" />
            {searchInput.length > 0 && (
              <IconButton aria-label="Clear search" variant="minimal" icon="cancel-circle-outline" size="small" />
            )}
          </Group>
        </SearchField>
      </Box>

      <Tabs size="xs" selectedId={activeTab} onChange={(id: string) => setActiveTab(id as HistoryTab)}>
        <Tabs.List px="16">
          {TAB_DEFINITIONS.map((tab) => (
            <Tabs.Tab key={tab.id} id={tab.id}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {TAB_DEFINITIONS.map((tab) => (
          <Tabs.Panel key={tab.id} tabId={tab.id} scrollable>
            <ConversationList
              conversations={displayedConversations}
              loading={loading}
              error={!!error}
              onOpen={onOpenConversation}
              isOwnConversation={isOwnConversation}
              memberDisplayMap={memberDisplayMap}
              emptyMessage={emptyMessage}
            />
          </Tabs.Panel>
        ))}
      </Tabs>

      <Box flexShrink={0}>
        {aiChatLimitData?.aiChatLimitStatus && (
          <Box p="8">
            <LaunchpadButton
              variant="minimal"
              style={{
                width: '100%',
                justifyContent: 'space-between',
                padding: '6px 8px',
                borderRadius: '6px',
                border: `1px solid ${themeVars.static.divider.default}`,
              }}
              onPress={() => {
                if (usageHref) {
                  window.open(usageHref, '_blank');
                }
              }}
            >
              <Stack direction="row" gap="6" alignItems="center" cssClass={styles.secondaryText}>
                <Icon name="warning" size="small" className={styles.secondaryIcon} />
                <Text size="small">
                  {aiChatLimitData.aiChatLimitStatus.current_count}
                  {aiChatLimitData.aiChatLimitStatus.limit > 0
                    ? ` / ${aiChatLimitData.aiChatLimitStatus.limit}`
                    : ''}{' '}
                  prompts used
                </Text>
              </Stack>
              {usageHref && <Icon name="link-external" size="small" />}
            </LaunchpadButton>
          </Box>
        )}

        <SearchPagination
          page={page}
          setPage={setPage}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          loading={loading}
        />
      </Box>
    </Box>
  );
};

interface MemberInfo {
  displayName: string;
  email: string;
  initials: string | undefined;
}

interface ConversationListProps {
  conversations: ConversationSummary[];
  loading: boolean;
  error: boolean;
  onOpen: (conversationId: string) => void;
  isOwnConversation: (ownerValue: string) => boolean;
  memberDisplayMap: Map<string, MemberInfo>;
  emptyMessage: string;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  loading,
  error,
  onOpen,
  isOwnConversation,
  memberDisplayMap,
  emptyMessage,
}) => (
  <Box overflow="auto" flexGrow={1} p="16">
    {loading && (
      <Box padding="24" display="flex" justifyContent="center">
        <Text>Loading conversations...</Text>
      </Box>
    )}

    {!loading && error && (
      <Box padding="24" display="flex" justifyContent="center">
        <Text color="weak">Failed to load conversations</Text>
      </Box>
    )}

    {!loading && !error && conversations.length === 0 && (
      <Box padding="24" display="flex" justifyContent="center">
        <Text color="weak">{emptyMessage}</Text>
      </Box>
    )}

    {!loading && !error && conversations.length > 0 && (
      <Stack gap="4">
        {conversations.map((conversation) => (
          <ConversationCard
            key={conversation.conversation_id}
            conversation={conversation}
            onOpen={onOpen}
            isOwnConversation={isOwnConversation}
            memberDisplayMap={memberDisplayMap}
          />
        ))}
      </Stack>
    )}
  </Box>
);

interface ConversationSummary {
  conversation_id: string;
  user_email: string;
  user_message_count: number;
  agent_message_count: number;
  initiated_at: string;
  message_preview: string;
  title?: string | null;
  status: string;
  is_shared: boolean;
  tags?: Array<{ key: string; value: string }> | null;
}

interface ConversationCardProps {
  conversation: ConversationSummary;
  onOpen: (conversationId: string) => void;
  isOwnConversation: (ownerValue: string) => boolean;
  memberDisplayMap: Map<string, MemberInfo>;
}

const TAG_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '2px 6px',
  borderRadius: '4px',
  lineHeight: '18px',
  border: `1px solid ${themeVars.static.divider.weak}`,
  backgroundColor: themeVars.static.surface.default,
} as const;

interface StatusVisual {
  // When true, render the pulsing live indicator instead of `icon`.
  live?: boolean;
  icon?: IconName;
  color: string;
  label: string;
}

// Mirrors the `agent_conversations.status` values surfaced on AIConversationSummary:
//   "active" (in-progress), "completed", "stopped" (user cancelled), "failed"
// `isStaleActive` flags conversations still marked active long past the point one
// could realistically be running (see STALE_ACTIVE_THRESHOLD_MS) — those get a
// warning rather than the live indicator. Falls back to a neutral completed
// indicator for any unknown future status so the card still renders something.
function getStatusVisual(status: string, isStaleActive: boolean): StatusVisual {
  switch (status) {
    case 'active':
      if (isStaleActive) {
        return {
          icon: 'warning-outline',
          color: themeVars.static.content.sentiment.caution,
          label: 'Active for over 90 minutes — this chat may have stalled',
        };
      }
      return {
        live: true,
        color: themeVars.static.content.sentiment.informative,
        label: 'In progress',
      };
    case 'stopped':
      return {
        icon: 'stop-circle-outline',
        color: themeVars.static.content.weak,
        label: 'Stopped',
      };
    case 'failed':
      return {
        icon: 'warning-outline',
        color: themeVars.static.content.sentiment.caution,
        label: 'Failed',
      };
    case 'completed':
    default:
      return {
        icon: 'check-circle-outline',
        color: themeVars.static.content.weak,
        label: 'Completed',
      };
  }
}

interface PrTagVisual {
  label: string;
  tooltipLabel: string;
  icon: IconName;
}

const ConversationCard: React.FC<ConversationCardProps> = ({
  conversation,
  onOpen,
  isOwnConversation,
  memberDisplayMap,
}) => {
  const isMine = isOwnConversation(conversation.user_email);
  const ownerValue = conversation.user_email;
  const memberInfo = memberDisplayMap.get(ownerValue);
  const ownerDisplayName = isEmail(ownerValue) ? ownerValue : (memberInfo?.displayName ?? 'another user');
  const avatarEmail = isEmail(ownerValue) ? ownerValue : memberInfo?.email;
  const avatarInitials = isEmail(ownerValue) ? undefined : memberInfo?.initials;
  const title = deriveTitle(conversation);
  const initiatedAtMs = new Date(conversation.initiated_at).getTime();
  const isStaleActive =
    conversation.status === 'active' &&
    Number.isFinite(initiatedAtMs) &&
    Date.now() - initiatedAtMs > STALE_ACTIVE_THRESHOLD_MS;
  const statusVisual = getStatusVisual(conversation.status, isStaleActive);

  const prTag = conversation.tags?.find((t) => t.key === CONVERSATION_TAG_KEYS.prFiled);
  // The pr-filed value is attacker-controlled (set via the public tagConversation mutation), so it
  // must be validated as an http(s) URL before it is ever opened — otherwise a `javascript:` value
  // would run in the app origin when clicked. See SEC-8216.
  const prHref = getSafeHttpUrl(prTag?.value);
  const prMergedTag = conversation.tags?.find((t) => t.key === CONVERSATION_TAG_KEYS.prMerged);
  const prClosedTag = conversation.tags?.find((t) => t.key === CONVERSATION_TAG_KEYS.prClosed);
  const isAutoInvestigation = conversation.tags?.some(
    (t) => t.key === CONVERSATION_TAG_KEYS.autoInvestigation && t.value === 'true',
  );
  const flagCleanupTag = conversation.tags?.find((t) => t.key === CONVERSATION_TAG_KEYS.flagCleanupInProgress);
  const automatedFlagCleanupTag = conversation.tags?.find((t) => t.key === CONVERSATION_TAG_KEYS.automatedFlagCleanup);

  let prTagVisual: PrTagVisual | undefined;
  if (prMergedTag) {
    prTagVisual = { label: 'PR merged', tooltipLabel: 'PR merged', icon: 'check-circle' };
  } else if (prClosedTag) {
    prTagVisual = { label: 'PR closed', tooltipLabel: 'PR closed without merging', icon: 'cancel' };
  } else if (prTag) {
    prTagVisual = { label: 'PR opened', tooltipLabel: 'PR opened', icon: 'arrow-merge' };
  }

  return (
    <Box
      p="8"
      borderRadius="8"
      display="flex"
      flexDirection="row"
      gap="8"
      style={{
        cursor: 'pointer',
        alignItems: 'flex-start',
      }}
      onClick={() => onOpen(conversation.conversation_id)}
    >
      <Box
        flexShrink={0}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '20px',
          width: '16px',
        }}
      >
        <TooltipTrigger>
          <LaunchpadButton
            variant="minimal"
            aria-label={statusVisual.label}
            style={{
              padding: 0,
              minWidth: 0,
              minHeight: 0,
              background: 'transparent',
              border: 'none',
              color: statusVisual.color,
              display: 'inline-flex',
              cursor: 'pointer',
            }}
          >
            {statusVisual.live ? (
              <LiveModeIndicator />
            ) : (
              statusVisual.icon && <Icon name={statusVisual.icon} size="small" />
            )}
          </LaunchpadButton>
          <Tooltip placement="bottom">{statusVisual.label}</Tooltip>
        </TooltipTrigger>
      </Box>

      <Box display="flex" flexDirection="column" gap="4" flexGrow={1} style={{ minWidth: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap="8">
          <Text size="small" weight="medium" cssClass={styles.truncatedText}>
            {title}
          </Text>
          {!isMine && (
            <Box display="flex" alignItems="center" flexShrink={0} title={`Shared by ${ownerDisplayName}`}>
              <MemberAvatar
                email={avatarEmail}
                initials={avatarInitials}
                size="small"
                ariaLabel={`Shared by ${ownerDisplayName}`}
              />
            </Box>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap="6" style={{ flexWrap: 'wrap' }}>
          <Text size="small" color="weak">
            {formatRelativeDate(conversation.initiated_at)}
          </Text>

          {prTagVisual && prHref && (
            <ConversationTagChip
              icon={prTagVisual.icon}
              label={prTagVisual.label}
              tooltip={prTagVisual.tooltipLabel}
              href={prHref}
            />
          )}

          {isAutoInvestigation && (
            <ConversationTagChip icon="sparkles" label="Auto-investigation" tooltip="Started automatically" />
          )}

          {flagCleanupTag && (
            <ConversationTagChip
              icon="flag-code"
              label="Flag cleanup"
              tooltip={`Flag cleanup: ${flagCleanupTag.value}`}
            />
          )}
          {automatedFlagCleanupTag && (
            <ConversationTagChip
              icon="sparkles"
              label="Automated cleanup"
              tooltip={`Automated cleanup: ${automatedFlagCleanupTag.value}`}
            />
          )}

          {conversation.is_shared && <ConversationTagChip icon="link" label="Shared" tooltip="Shared" />}
        </Box>
      </Box>
    </Box>
  );
};

interface ConversationTagChipProps {
  icon: IconName;
  label: string;
  tooltip: ReactNode;
  href?: string;
}

const ConversationTagChip: React.FC<ConversationTagChipProps> = ({ icon, label, tooltip, href }) => {
  const content = (
    <>
      <Icon name={icon} size="small" />
      <Text size="small" color="weak">
        {label}
      </Text>
    </>
  );

  if (href) {
    return (
      <TooltipTrigger>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          // Stop propagation so clicking a tag (e.g. PR) opens that link
          // without ALSO opening the surrounding conversation card.
          onClick={(e) => e.stopPropagation()}
          style={{ ...TAG_STYLE, textDecoration: 'none', cursor: 'pointer' }}
        >
          {content}
        </a>
        <Tooltip placement="bottom">{tooltip}</Tooltip>
      </TooltipTrigger>
    );
  }

  // For display-only chips we still use a Button so the tooltip's focusable
  // trigger contract is satisfied. With no onPress, the click bubbles up to
  // the conversation card's onClick — which opens the conversation, matching
  // the rest of the row's behavior.
  return (
    <TooltipTrigger>
      <LaunchpadButton variant="minimal" aria-label={label} style={{ ...TAG_STYLE, cursor: 'pointer' }}>
        {content}
      </LaunchpadButton>
      <Tooltip placement="bottom">{tooltip}</Tooltip>
    </TooltipTrigger>
  );
};
